import React, { useState } from 'react';
import { FlatList, Pressable, View } from 'react-native';
import { Badge, Button, Checkbox, Field, Icon, Modal, Text, TextField, useInputStyle, useTheme, type Option } from '../../../ui';

/** Field showing selected values as badges; opens a searchable checkbox list. */
export function MultiSelect<V extends string | number>({
  label,
  value,
  options,
  onChange,
  placeholder = 'Any',
  hint,
  disabled,
  testID,
}: {
  label?: string;
  value: V[];
  options: Option<V>[];
  onChange: (v: V[]) => void;
  placeholder?: string;
  hint?: string;
  disabled?: boolean;
  testID?: string;
}) {
  const { colors } = useTheme();
  const base = useInputStyle(false, disabled);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const selected = options.filter((o) => value.includes(o.value));
  const shown = q ? options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase())) : options;
  const toggle = (v: V, on: boolean) => onChange(on ? [...value, v] : value.filter((x) => x !== v));
  return (
    <Field label={label} hint={hint}>
      <Pressable
        testID={testID}
        accessibilityRole="button"
        accessibilityLabel={`${label ?? 'Select'}: ${selected.length ? selected.map((s) => s.label).join(', ') : placeholder}`}
        disabled={disabled}
        onPress={() => setOpen(true)}
        style={[base, { flexDirection: 'row', alignItems: 'center', gap: 6, flexWrap: 'wrap', paddingVertical: 6 }]}
      >
        <View style={{ flex: 1, flexDirection: 'row', flexWrap: 'wrap', gap: 4 }}>
          {selected.length ? (
            selected.map((s) => (
              <Badge key={String(s.value)} tone="primary">
                {s.label}
              </Badge>
            ))
          ) : (
            <Text color={colors.faint}>{placeholder}</Text>
          )}
        </View>
        <Icon name="chevronDown" size={16} color={colors.muted} />
      </Pressable>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={label ?? 'Select'}
        width={440}
        noScroll
        footer={
          <>
            <Button title="Clear" variant="ghost" onPress={() => onChange([])} />
            <Button title="Done" variant="primary" onPress={() => setOpen(false)} />
          </>
        }
      >
        {options.length > 8 && (
          <View style={{ padding: 12, paddingBottom: 4 }}>
            <TextField placeholder="Search…" value={q} onChangeText={setQ} accessibilityLabel="Search options" />
          </View>
        )}
        <FlatList
          data={shown}
          keyExtractor={(o) => String(o.value)}
          style={{ flexGrow: 0 }}
          contentContainerStyle={{ padding: 12, gap: 12 }}
          renderItem={({ item }) => <Checkbox label={item.label} value={value.includes(item.value)} onChange={(on) => toggle(item.value, on)} />}
          ListEmptyComponent={<Text variant="muted">No options</Text>}
        />
      </Modal>
    </Field>
  );
}
